import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription, forkJoin } from 'rxjs';
import { ConstantService, ThemeEnum } from '../../services/common/constant.service';
import { EnrollmentOptionsService } from '../../services/common/enrollment-options';
import { AuthMethodViewModel, AuthMethodsService, AuthenticationMethodConfigurationService, AuthenticationMethodConfigurationViewModel } from '../../services/identity';
import { BaseComponent } from '../../shared/enrollment/methods/base/base.component';
import { FormStatusData } from '../../shared/enrollment/enrollment.component';
import { EnrollmentInputModel } from '../../shared/enrollment/models/enrollmentinput.model';
import { AuthService } from '../auth.service';

@Component({
  selector: 'app-awaiting-enrollment',
  standalone: false,
  templateUrl: './awaiting-enrollment.component.html',
  styleUrl: './awaiting-enrollment.component.scss'
})
export class AwaitingEnrollmentComponent implements OnInit, OnDestroy {

  private subscriptions = new Subscription();

  userId: number;
  username: string;
  orgId: number;
  orgName: string;

  logoImage: string;

  private isLoaded: boolean;
  private isSaving: boolean;

  private methods: AuthMethodViewModel[];
  private softTokenMethod: AuthMethodViewModel;
  private softTokenValid: boolean;
  private fidoMethod: AuthMethodViewModel;
  private fidoValid: boolean;
  private allMethodsValid: boolean;

  private options: EnrollmentInputModel;

  @ViewChild('softTokenComponent') softTokenComponent: BaseComponent;
  @ViewChild('fidoComponent') fidoComponent: BaseComponent;

  constructor(
    private authService: AuthService,
    private constantService: ConstantService,
    private authMethodsService: AuthMethodsService,
    private enrollmentOptions: EnrollmentOptionsService,
    private authMethodConfigService: AuthenticationMethodConfigurationService,
    private toastrService: ToastrService,
    private translateService: TranslateService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userId = +this.authService.user.profile.UserID;
    this.username = this.authService.userName;
    this.orgId = +this.authService.organizationId;
    this.orgName = this.constantService.getSubdomain();


    this.options = await this.enrollmentOptions.createOptions(this.orgId, this.orgName, this.userId, this.username, true, this.authService.userType, false, false, false, false, false, false, false);

    const logoImage = this.constantService.getImageFromServer(ThemeEnum.companyLogo);
    this.logoImage = logoImage ?? ThemeEnum.companyLogoDefault;

    this.subscriptions.add(
      forkJoin(
        [
          this.authMethodConfigService.apiAuthenticationMethodConfigurationForUserOrganizationIdUserIdGet(this.options.org.id, this.options.user.id),
          this.authMethodsService.apiAuthMethodsAwaitingEnrollmentGet(),
          this.options.functions.getOrgSettings(this.options.org.id, 'MFA')
        ])
        .subscribe({
          next: ([config, methods, mfaOptions]) => {
            if (methods?.length == 0) {
              this.redirectNext();
            }
            this.methods = methods;
            this.softTokenMethod = this.methods.find(x => x.type === 'SoftToken');
            if (!this.softTokenMethod) {
              this.softTokenValid = true;
            }
            this.fidoMethod = this.methods.find(x => x.type === 'Fido');
            if (!this.fidoMethod) {
              this.fidoValid = true;
            }

            this.handleMfaOptions(config, mfaOptions);
          },
          error: error => { }
        }));
  }

  handleMfaOptions(authConfig: AuthenticationMethodConfigurationViewModel, mfaOptions: any) {
    let userCodeFormat = JSON.parse(mfaOptions.find((x: any) => x.key == 'UserCodeFormat')?.value).MinimumLength;
    let fidoAllowMultiple = JSON.parse(mfaOptions.find((x: any) => x.key == 'FidoAllowMultiple')?.value ?? 'false');
    let fidoAllowUsernameless = JSON.parse(mfaOptions.find((x: any) => x.key == 'FidoAllowResidentKey')?.value ?? 'false');
    let softTokenType = mfaOptions.find((x: any) => x.key == 'SoftTokenAuthenticatorType')?.value ?? 'securenvoy';
    let pushAllowed = !JSON.parse(mfaOptions.find((x: any) => x.key == 'DisableSoftTokenPush')?.value ?? 'false');

    this.options.state.staticCode = {
      minLength: userCodeFormat
    }

    this.options.state.softToken = {
      pushEnabled: true,
      pushEnforced: authConfig.enforcePush,
      pushAllowed: pushAllowed,
      authenticatorType: softTokenType,
    }

    this.options.state.fido = {
      allowMultipleTokens: fidoAllowMultiple,
      allowResidentKey: fidoAllowUsernameless
    }
    this.isLoaded = true;
  }

  logout(): void {
    this.authService.signOut();
  }

  private onSoftTokenFormStatusChange(data: FormStatusData): void {
    this.softTokenValid = data.status == 'VALID';
    this.updateAllValid();
  }

  private onFidoFormStatusChange(data: FormStatusData): void {
    this.fidoValid = data.status == 'VALID';
    this.updateAllValid();
  }

  private updateAllValid() {
    this.allMethodsValid = this.softTokenValid && this.fidoValid;
  }

  private redirectNext() {
    this.authService.getUserPostLoginStatus();
  }

  private save() {
    let updatedMethods: AuthMethodViewModel[] = [];
    if (this.softTokenMethod) {
      updatedMethods.push(this.softTokenComponent.getMethod());
    }
    if (this.fidoMethod) {
      updatedMethods.push(this.fidoComponent.getMethod());
    }
    this.subscriptions.add(
      this.authMethodsService.apiAuthMethodsAwaitingEnrollmentPost(updatedMethods)
        .subscribe({
          next: data => {
            this.toastrService.success(this.translateService.instant('Action.Success'));
            this.isSaving = false;
            this.redirectNext();
          },
          error: error => {
            if (error?.error[0]?.message) {
              this.toastrService.clear();
              this.toastrService.error(error?.error[0]?.message, this.translateService.instant('Action.Error'), this.constantService.ToastError);
            }
            this.isSaving = false;
          }
        })
    )
  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

}
